<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productPrice.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productPrice.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button outline color="primary" @click.prevent="onShowForm">{{
              $t('copy')
            }}</ts-button>
            <ts-button outline color="info" @click.prevent="update">{{
              $t('update')
            }}</ts-button>
            <ts-button
              color="success"
              @click.prevent="onShowExport"
              :waiting="exporting"
              class="ml-2"
            >
              <i class="fas fa-file-excel"></i> {{ $t('export') }}
            </ts-button>
            <ts-button
              color="success"
              @click.prevent="onShowImport"
              class="ml-2"
            >
              <i class="fas fa fa-upload"></i> {{ $t('import') }}
            </ts-button>
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label>{{ $t('productPrice.branchName') }}</label>
                  <a-select
                    v-model="branch_id"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    :options="branches"
                    class="ts-w-full"
                  ></a-select>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('productPrice.category') }}</label>
                  <a-select
                    show-search
                    mode="multiple"
                    :placeholder="$t('all')"
                    v-model="model.category_id"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    :options="categories"
                    class="ts-w-full"
                  ></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :data="resources" 
				:columns="columns" 
				size="small"
				highlight-row
        stripe
				max-height="700"
        :loading="loading"
      >
        <template slot-scope="{ row }" slot="productNameKh">
          <td class="ts-font-khmer" style="border-bottom: none;">{{ row.product_name_kh }}</td>
        </template>
        <template slot-scope="{ row }" slot="cost">
            {{ $defaultCurrencyWithCode(row.cost, 8) }}
        </template>
        <template slot-scope="{ row }" slot="dealerPrice">
            {{ $defaultCurrencyWithCode(row.dealer_price, 8) }}
        </template>
        <template slot-scope="{ row }" slot="salePrice">
            {{ $defaultCurrencyWithCode(row.sale_price, 8) }}
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <a-modal
        v-model="show_form"
        :centered="true"
        :closable="false"
        :zIndex="1020"
        :title="$t('productPrice.copyFrom')"
        :okText="$t('yes')"
        :cancelText="$t('no')"
        @ok="onCopyAction"
        @cancel="clearInput"
        :confirmLoading="waiting"
        width="350px"
      >
        <ts-loading-banner :loading="loading_branch">
          <div class="row">
            <div class="col-md-12">
              <label class="required">{{
                $t('productPrice.branchName')
              }}</label>
              <a-select
                v-model="model.copy_from_branch_id"
                showSearch
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :options="branches"
                class="ts-w-full"
              ></a-select>
              <div class="validate" v-if="errors.has('copy_from_branch_id')">
                {{ errors.first('copy_from_branch_id') }}
              </div>
            </div>
          </div>
        </ts-loading-banner>
      </a-modal>
    </ts-card>
    <a-modal
      v-model="show_export_form"
      :centered="true"
      :closable="false"
      :zIndex="1020"
      :title="$t('export')"
      :okText="$t('yes')"
      :cancelText="$t('no')"
      @ok="onExport"
      @cancel="clearInput"
      :confirmLoading="exporting"
      width="350px"
    >
      <ts-loading-banner :loading="loading_branch" class="ts-space-y-2">
        <div class="row">
          <div class="col-md-12">
            <label>{{ $t('productPrice.category') }}</label>
            <a-select
              show-search
              mode="multiple"
              :placeholder="$t('all')"
              v-model="model.export_category_id"
              :max-tag-count="2"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              :options="categories"
              class="ts-w-full"
              @change="getGroup()"
            ></a-select>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label>{{ $t('productPrice.group') }}</label>
            <a-select
              show-search
              mode="multiple"
              :placeholder="$t('all')"
              v-model="model.export_pro_group_id"
              :max-tag-count="2"
              :loading="loading_group"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              :options="groups"
              class="ts-w-full"
            ></a-select>
            <div class="validate" v-if="errors.has('export_grade')">
              {{ errors.first('export_grade') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required">{{ $t('productPrice.grade') }}</label>
            <a-select
              show-search
              mode="multiple"
              v-model="model.export_grade"
              :max-tag-count="2"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              class="ts-w-full"
            >
              <a-select-option value="A">A</a-select-option>
              <a-select-option value="B">B</a-select-option>
              <a-select-option value="C">C</a-select-option>
              <a-select-option value="D">D</a-select-option>
            </a-select>
            <div class="validate" v-if="errors.has('export_grade')">
              {{ errors.first('export_grade') }}
            </div>
          </div>
        </div>
      </ts-loading-banner>
    </a-modal>
    <a-modal
      :title="$t('import')"
      v-if="show_import_form"
      v-model="show_import_form"
      :visible="show_import_form"
      @cancel="() => (show_import_form = false)"
      width="350px"
      :footer="null"
      :maskClosable="false"
      :centered="true"
    >
      <ImportProductPrice @cancel="onImportCancel()" />
    </a-modal>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'
import ImportProductPrice from './importProductPrice.vue'

export default {
  name: 'productPrice',
  components: {
    ImportProductPrice
  },
  data () {
    return {
      loading: false,
      show_form: false,
      show_export_form: false,
      show_import_form: false,
      loading_branch: false,
      loading_group: false,
      waiting: false,
      exporting: false,
      importing: false,
      errors: new Errors(),
      branches: [],
      categories: [],
      groups: [],
      model: {
        copy_from_branch_id: null,
        category_id: [],
        export_category_id: [],
        export_pro_group_id: [],
        export_grade: ['A']
      }
    }
  },
  created () {
    this.fetchData()
    this.getBranch()
    this.getCategory()
  },
  computed: {
    ...mapState('product/price', ['resources', 'pagination']),
    search: {
      get () {
        return this.$store.state.product.price.search
      },
      set (newValue) {
        this.$store.commit('product/price/SET_SEARCH', newValue)
        this.$store.commit('product/price/RESET_CURRENT_PAGE')
      }
    },
    branch_id: {
      get () {
        return this.$store.state.product.price.branch_id
      },
      set (newValue) {
        this.$store.commit('product/price/SET_BRANCH_ID', newValue)
      }
    },
    columns () {
      return [
        { 
          title: this.$t('productPrice.branchName'), 
          width: 100,
          key: 'branch_name_en' 
        },
        { 
          title: this.$t('productPrice.category'),
          width: 180, 
          key: 'category_name_en' 
        },
        { 
          title: this.$t('productPrice.group'),
          width: 160, 
          key: 'pro_group_name' 
        },
        { 
          title: this.$t('productPrice.barcode'),
          width: 120, 
          key: 'barcode' 
        },
        { 
          title: this.$t('productPrice.productNameKh'),
          minWidth: 320,
          slot: 'productNameKh' 
        },
        { title: this.$t('productPrice.productNameEn'),
          minWidth: 320, 
          key: 'product_name_en' 
        },
        { title: this.$t('productPrice.saleCode'),
          minWidth: 150, 
          key: 'sale_code' 
        },
        { title: this.$t('productPrice.uom'), 
          width: 100,
          key: 'uom_name_en' 
        },
        {
          title: this.$t('productPrice.grade'),
          align: 'center', 
          width: 80,
          key: 'grade' 
        },
        { 
          title: this.$t('productPrice.cost'),
          slot: 'cost', 
          width: 150,
          align: 'right' 
        },
        {
          title: this.$t('productPrice.dealerPrice'),
          slot: 'dealerPrice',
          width: 150,
          align: 'right'
        },
        { 
          title: this.$t('productPrice.salePrice'),
          slot: 'salePrice',
          width: 150, 
          align: 'right' 
        }
      ]
    }
  },
  methods: {
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch('product/price/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    getCategory () {
      this.$store.dispatch('product/price/getCategory').then(response => {
        this.categories = response.data.map(el => ({
          value: el.category_id,
          label: el.category_name_en
        }))
      })
    },
    getGroup () {
      this.loading_group = true
      this.export_pro_group_id = []
      this.$store
        .dispatch('product/price/getGroup', {
          category_id: this.model.export_category_id
        })
        .then(response => {
          this.groups = response.data.map(el => ({
            value: el.pro_group_id,
            label: el.pro_group_name_en
          }))
        })
        .finally(() => {
          this.loading_group = false
        })
    },
    getBranch () {
      this.loading_branch = true
      this.$store
        .dispatch('product/price/getBranch')
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
        .finally(() => {
          this.loading_branch = false
        })
    },
    onShowForm () {
      this.show_form = true
      this.getBranch()
    },

    onShowImport () {
      this.importing = true
      this.show_import_form = true
    },
    onImportCancel () {
      this.show_import_form = false
      this.importing = false
      this.fetchData()
    },
    onShowExport () {
      this.show_export_form = true
    },
    onExport () {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('product/price/exportExel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
          this.show_export_form = false
          this.clearInput()
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.exporting = false
        })
    },
    onCopyAction () {
      this.waiting = true
      this.$store
        .dispatch('product/price/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
          this.clearInput()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    clearInput () {
      this.show_form = false
      this.errors = new Errors()
      this.model.copy_from_branch_id = null
      this.model.category_id = []
      this.model.export_category_id = []
      this.model.export_pro_group_id = []
      this.model.export_grade = ['A']
    },
    update () {
      this.$router.push({ name: 'product-price-update' })
    },
    categoryFilter () {
      this.$store.commit('product/price/SET_CATEGORIES', this.model.category_id)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    'model.category_id': debounce(function () {
      this.categoryFilter()
    }, 500),
    branch_id: debounce(function () {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('product/price/RESET_STATE')
    next()
  }
}
</script>
